import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { Heart, MapPin, CalendarDays, ExternalLink, Music, ArrowUpRight, ShoppingBag } from "lucide-react";
import { formatDate, eventPriceRange } from "@/lib/format";

export default function ArtistPage() {
    const { id } = useParams();
    const { fan, isFollowing, follow, unfollow } = useFan();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setLoading(true);
        endpoints
            .getArtist(id)
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [id]);

    const following = isFollowing(id);

    const toggleFollow = async () => {
        if (!fan) {
            toast.error("Sign in as a fan to follow");
            return;
        }
        setBusy(true);
        try {
            if (following) {
                await unfollow(id);
                setData((d) => (d ? { ...d, artist: { ...d.artist, followers: Math.max(0, (d.artist.followers || 1) - 1) } } : d));
                toast(`Unfollowed ${data.artist.name}`);
            } else {
                await follow(id, "artist_page");
                setData((d) => (d ? { ...d, artist: { ...d.artist, followers: (d.artist.followers || 0) + 1 } } : d));
                toast(`Now following ${data.artist.name}`);
            }
        } catch {
            toast.error("Could not update follow. Try again.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-24 font-mono text-sm text-[#666]" data-testid="artist-loading">Loading…</div>;
    }
    if (!data) return null;

    const a = data.artist;

    return (
        <div data-testid="artist-page" className="pm-fade">
            {/* HERO */}
            <section className="relative border-b border-[#2a2a2a]">
                <div className="relative h-[45vh] md:h-[60vh] overflow-hidden">
                    <img src={a.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
                </div>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 -mt-32 md:-mt-40 relative z-10 pb-8">
                    <div className="flex flex-col md:flex-row md:items-end gap-6">
                        <img
                            src={a.image_url}
                            alt=""
                            className="w-40 h-40 md:w-56 md:h-56 object-cover border-4 border-[#0a0a0a] grayscale hover:grayscale-0 transition-all"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                                {a.genre}
                            </div>
                            <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-none mt-1" data-testid="artist-name">
                                {a.name}
                            </h1>
                            <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#a3a3a3]">
                                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {a.city}, {a.country}</span>
                                <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {a.followers.toLocaleString()} fans</span>
                                <span className="font-mono uppercase text-xs">{a.is_local ? "Local" : "International"}</span>
                            </div>
                        </div>
                        <button
                            onClick={toggleFollow}
                            disabled={busy}
                            className={`pm-btn ${following ? "" : "pm-btn-primary"}`}
                            data-testid="artist-follow-btn"
                        >
                            <Heart className="w-4 h-4 mr-2" fill={following ? "currentColor" : "none"} />
                            {following ? "Following" : "Follow"}
                        </button>
                    </div>
                </div>
            </section>

            {/* BIO */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-2">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Biography</div>
                        <p className="mt-3 text-[#f4f4f0] text-lg leading-relaxed max-w-2xl">{a.bio}</p>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Listen</div>
                        <div className="mt-3 space-y-2">
                            {(a.music_links || []).map((l) => (
                                <a
                                    key={l.platform}
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`artist-music-link-${l.platform.toLowerCase()}`}
                                    className="flex items-center justify-between border border-[#2a2a2a] px-4 py-3 hover:border-[#ff4400] group"
                                >
                                    <span className="flex items-center gap-2 font-display font-bold text-sm uppercase">
                                        <Music className="w-4 h-4 text-[#ff4400]" /> {l.platform}
                                    </span>
                                    <ExternalLink className="w-4 h-4 text-[#666] group-hover:text-[#ff4400]" />
                                </a>
                            ))}
                        </div>
                        <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-[#666]">Social</div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {(a.social_links || []).map((l) => (
                                <a
                                    key={l.id || l.platform}
                                    href={l.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`artist-social-${(l.platform || "link").toLowerCase()}`}
                                    className="px-3 py-2 text-xs font-mono uppercase border border-[#2a2a2a] hover:border-[#ff4400] hover:text-[#ff4400]"
                                >
                                    {l.platform}
                                </a>
                            ))}
                            {a.website && (
                                <a href={a.website} target="_blank" rel="noopener noreferrer" data-testid="artist-website-link" className="px-3 py-2 text-xs font-mono uppercase border border-[#2a2a2a] hover:border-[#ff4400]">Website</a>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Upcoming events */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">04 · Live</div>
                    <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-3xl md:text-5xl">
                        Upcoming events
                    </h2>
                    {data.upcoming_events.length === 0 ? (
                        <div className="mt-6 border border-[#2a2a2a] p-10 text-center text-[#a3a3a3]" data-testid="artist-no-upcoming">
                            No upcoming events yet. Follow to hear first.
                        </div>
                    ) : (
                        <div className="mt-6 border border-[#2a2a2a]">
                            {data.upcoming_events.map((e) => (
                                <EventRow key={e.id} event={e} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Past events */}
            {data.past_events.length > 0 && (
                <section className="border-b border-[#2a2a2a]">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Archive</div>
                        <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-2xl md:text-4xl">Past events</h2>
                        <div className="mt-6 border border-[#2a2a2a]">
                            {data.past_events.map((e) => (
                                <EventRow key={e.id} event={e} muted />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Albums */}
            {a.albums?.length > 0 && (
                <section className="border-b border-[#2a2a2a]">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Discography</div>
                        <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-2xl md:text-4xl">Albums & singles</h2>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                            {a.albums.map((al) => (
                                <div key={al.id} className="group" data-testid={`artist-album-${al.id}`}>
                                    <div className="relative aspect-square overflow-hidden">
                                        {al.external_url ? (
                                            <a href={al.external_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                                                <img src={al.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                                            </a>
                                        ) : (
                                            <img src={al.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                                        )}
                                    </div>
                                    <div className="mt-2 font-display font-bold text-sm uppercase">{al.title}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">{al.release_type || al.type} · {al.year}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Merch */}
            {a.merch?.length > 0 && (
                <section className="border-b border-[#2a2a2a]">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                        <div className="flex items-end justify-between">
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Merch</div>
                                <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-2xl md:text-4xl">Shop</h2>
                            </div>
                            {a.shopify_url && (
                                <a href={a.shopify_url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono uppercase tracking-widest text-[#a3a3a3] hover:text-[#ff4400] flex items-center gap-1" data-testid="artist-shopify-link">
                                    Full store <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                            {a.merch.filter((m) => m.active !== false).map((m) => (
                                <a
                                    key={m.id}
                                    href={m.shopify_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`artist-merch-${m.id}`}
                                    className="group border border-[#2a2a2a] p-3 hover:border-[#ff4400] block"
                                >
                                    <div className="relative aspect-square overflow-hidden">
                                        <img src={m.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                                    </div>
                                    <div className="mt-2 font-display font-bold text-sm">{m.title}</div>
                                    <div className="mt-1 flex items-center justify-between">
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">${m.price.toFixed(2)}</div>
                                        <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-[#ff4400]">
                                            <ShoppingBag className="w-3 h-3" /> Shopify
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                        <div className="mt-3 text-[10px] font-mono uppercase text-[#666]">
                            Purchases happen on Shopify. Project Mint does not process payments.
                        </div>
                    </div>
                </section>
            )}

            {/* Related */}
            {data.related_artists?.length > 0 && (
                <section>
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Also listening</div>
                        <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-2xl md:text-4xl">Related artists</h2>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                            {data.related_artists.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/artists/${r.id}`}
                                    data-testid={`related-artist-${r.id}`}
                                    className="group bg-[#0a0a0a] p-4 flex items-center gap-4 hover:bg-[#141414]"
                                >
                                    <img src={r.image_url} alt="" className="w-16 h-16 object-cover grayscale group-hover:grayscale-0 transition-all" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase text-sm group-hover:text-[#ff4400]">{r.name}</div>
                                        <div className="text-xs text-[#a3a3a3] mt-1">{r.genre} · {r.city}</div>
                                    </div>
                                    <ArrowUpRight className="w-4 h-4 text-[#666] group-hover:text-[#ff4400]" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

function EventRow({ event, muted = false }) {
    return (
        <Link
            to={`/events/${event.id}`}
            data-testid={`artist-event-row-${event.id}`}
            className={`flex flex-col md:flex-row md:items-center gap-3 md:gap-6 p-5 border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414] transition-colors ${muted ? "opacity-70" : ""}`}
        >
            <div className="text-[10px] font-mono uppercase tracking-widest bg-[#141414] border border-[#2a2a2a] px-3 py-2 md:w-32 text-center">
                <div className="text-[#ff4400] text-base font-display font-black">{formatDate(event.date).split(",")[0]}</div>
                <div className="text-[#a3a3a3]">{event.start_time}</div>
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-display font-black uppercase text-lg leading-tight">{event.name}</div>
                <div className="mt-1 text-xs text-[#a3a3a3] flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {event.venue_name} · {event.city}, {event.country}
                </div>
            </div>
            <div className="md:text-right">
                <div className="font-display font-black">{eventPriceRange(event)}</div>
                <div className="text-[10px] font-mono uppercase text-[#666]">{event.event_type}</div>
            </div>
        </Link>
    );
}
